import { Badge, ButtonV2, Checkbox, Input, Modal, Search, Spinner, TooltipV2 } from '@cimplico/components';
import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useCreateReferences } from '../../queries/use-create-references';
import { useFileRecords } from '../../queries/use-file-records';
import { FileRecordNew } from '../../types/file-record';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { useUserContext } from '../../providers/UserProvider';
import { hasWriteAccess, isFolderType } from './file-utils';
import ErrorSplashScreen from '../../pages/ErrorSplashScreen';
import plural from '../../utilities/plural';
import FilesBreadcrumbs from './FilesBreadcrumbs';
import RecordTypeIcon from './RecordTypeIcon';
import NoResults from '../../components/empty-state/NoResults';
import FyiLogo from '../../assets/fyi.png';

type NewReferencesWindowProps = {
    initialFolderId?: string;
    workpaperId?: string;
    fileRecord: FileRecordNew;
    onClose: () => void;
};

function NewReferencesWindow({ initialFolderId, workpaperId, fileRecord, onClose }: NewReferencesWindowProps): JSX.Element {
    const [currentFolderId, setCurrentFolderId] = useState(initialFolderId);
    const [selectedFolders, setSelectedFolders] = useState<FileRecordNew[]>([]);
    const [name, setName] = useState(fileRecord.name + ' - Reference');
    const [nameError, setNameError] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(999);
    const [search, setSearch] = useState('');

    const { isExternalUser } = useIsFirmRole();
    const { assignedWorkpapers } = useUserContext();

    const nameRef = useRef<HTMLInputElement>(null);
    const searchRef = useRef<HTMLInputElement | undefined>();

    const fileRecordsQuery = useFileRecords(page, limit, workpaperId, currentFolderId, '', 'sort[dir]=asc&sort[field]=name', search);
    const createFileReferencesQuery = useCreateReferences();

    function handleLevelSelect(value: string): void {
        if (searchRef.current) {
            searchRef.current.value = '';
        }
        setCurrentFolderId(value);
        setPage(1);
        setSearch('');
    }

    function handleRecordClick(record: FileRecordNew): void {
        if (searchRef.current) {
            searchRef.current.value = '';
        }
        if (isFolderType(record.recordType)) {
            setCurrentFolderId(record.id);
            setSearch('');
        }
    }

    function handleCheckboxClick(record: FileRecordNew): void {
        if (selectedFolders.find((folder) => folder.id === record.id)) {
            setSelectedFolders((prev) => [...prev.filter((value) => value.id !== record.id)]);
        } else {
            setSelectedFolders((prev) => [...prev, record]);
        }
    }

    function handleNameChange(name: string): void {
        setNameError('');
        setName(name);
    }

    function createReferences(): void {
        if (!name) {
            setNameError('File name must not be empty!');
            return;
        }

        createFileReferencesQuery.mutate(
            selectedFolders.map((record) => {
                return { sourceFileId: fileRecord.id, name, parentFolderId: record.id };
            }),
            {
                onSuccess: () => {
                    onClose();
                    toastNotification(
                        'success',
                        `${selectedFolders.length} new ${selectedFolders.length === 1 ? 'reference' : 'references'} created!`
                    );
                },
                onError: (err) => {
                    const results = err.response?.data?.results ?? [];
                    const errors = results.filter((r: { statusCode: number }) => r.statusCode >= 400);
                    const success = results.filter((r: { statusCode: number }) => r.statusCode < 400);

                    if (success.length > 0) {
                        toastNotification('success', `${success.length} new ${success.length === 1 ? 'reference' : 'references'} created!`);
                    }

                    const allErrorMessages = [];

                    for (const err of errors) {
                        const errFolder = err.error.original.parentFolderId;
                        const matchingFolderName = fileRecordsQuery.data?.data.records.find((r) => r.id === errFolder)?.name;

                        const errorMessage = `${`${matchingFolderName}: ` || ''}${err.error.fields.name}`;
                        allErrorMessages.push(errorMessage);
                    }

                    toastNotification(
                        'error',
                        `Unable to create ${errors.length} ${plural(errors.length, 'reference', 'references')}!`,
                        allErrorMessages
                    );
                    onClose();
                },
            }
        );
    }

    function handleAnimationComplete(): void {
        setTimeout(() => {
            nameRef.current?.focus();
        }, 1);
    }

    return (
        <Modal isOpen={true} onClose={onClose}>
            <motion.div
                className="relative"
                initial={false}
                transition={{ duration: 0.2 }}
                animate={{
                    minHeight: 450,
                    maxWidth: 1200,
                    height: fileRecordsQuery.isLoading ? 450 : 750,
                    width: fileRecordsQuery.isLoading ? 450 : '80vw',
                }}
            >
                {fileRecordsQuery.isLoading && (
                    <div className="flex items-center justify-center h-full">
                        <Spinner />
                    </div>
                )}
                {fileRecordsQuery.isError && (
                    <ErrorSplashScreen
                        title="An error has occurred."
                        leftArrow={true}
                        redirectTitle="Back to files"
                        handleClick={onClose}
                    />
                )}
                {fileRecordsQuery.isSuccess && (
                    <motion.div
                        className="flex flex-col h-full"
                        initial={{ opacity: 0 }}
                        onAnimationComplete={handleAnimationComplete}
                        transition={{ duration: 0, delay: fileRecordsQuery.isFetching ? 0 : 0.2 }}
                        animate={{ opacity: 1 }}
                    >
                        <div className="px-8 pt-6 flex flex-col gap-2 items-start">
                            <p className="text-lg leading-6 font-medium text-gray-900">Create References</p>
                            <p className="text-sm text-gray-500 text-left pb-2">
                                Select the folders where references for <b>{fileRecord.name}</b> should be created.
                                <br />
                                These references will point to the original file, they are not copies.
                            </p>
                            <div className="w-[80%] lg:w-[40%] text-left">
                                <Input
                                    size="small"
                                    type="text"
                                    label="Reference name"
                                    placeholder="Enter a file name..."
                                    errorMessage={nameError}
                                    onChange={(e): void => handleNameChange(e.target.value)}
                                    disabled={createFileReferencesQuery.isLoading}
                                    defaultValue={fileRecord.name + ' - Reference'}
                                    inputRef={nameRef}
                                />
                            </div>
                        </div>
                        <div className="px-8 py-6 flex flex-1 flex-col h-[400px]">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center [&>*]:border-b-0 [&>*]:p-0 border-b pb-1 gap-4">
                                {search || !fileRecordsQuery.data.data.path ? (
                                    <p className="text-sm text-gray-600">Searching in all folders...</p>
                                ) : (
                                    <div className="overflow-auto whitespace-nowrap w-full [&>*]:border-b-0 [&>*]:p-0">
                                        <FilesBreadcrumbs onLevelSelect={handleLevelSelect} path={fileRecordsQuery.data.data.path} />
                                    </div>
                                )}
                                <div className="justify-self-end w-full max-w-[280px]">
                                    <Search
                                        placeholder="Search here and sub-folders..."
                                        onChange={setSearch}
                                        defaultValue={search}
                                        inputRef={searchRef}
                                    />
                                </div>
                            </div>
                            <div className="pt-4 overflow-auto flex flex-col gap-2">
                                {fileRecordsQuery.data.data.records.length === 0 && (
                                    <>
                                        {!search ? (
                                            <p className="text-gray-500 text-sm text-center mt-4">No file records.</p>
                                        ) : (
                                            <NoResults search={search} />
                                        )}
                                    </>
                                )}
                                {fileRecordsQuery.data.data.records.map((record) => {
                                    const isFolder = isFolderType(record.recordType);
                                    const canAddReference = hasWriteAccess(isExternalUser, assignedWorkpapers, [record.path]);
                                    const isCheckboxDisabled = record.provider === 'FYI' || record.locked || !isFolder || !canAddReference;

                                    return (
                                        <div key={record.id} className="flex gap-2 pl-2">
                                            <Checkbox
                                                disabled={createFileReferencesQuery.isLoading || isCheckboxDisabled}
                                                checked={Boolean(selectedFolders.find((f) => f.id === record.id))}
                                                onChange={(): void => handleCheckboxClick(record)}
                                            />
                                            {isFolder ? (
                                                <FolderRow
                                                    showPath={Boolean(search)}
                                                    record={record}
                                                    onFolderClick={handleRecordClick}
                                                    onBreadCrumbsClick={handleLevelSelect}
                                                />
                                            ) : (
                                                <OtherFileRow
                                                    record={record}
                                                    showPath={Boolean(search)}
                                                    onBreadCrumbsClick={handleLevelSelect}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="bg-gray-50 py-3 flex flex-row items-center justify-between flex-wrap px-4 rounded-b-md">
                            <p className="text-gray-500 text-sm">
                                {selectedFolders.length} {plural(selectedFolders.length, 'folder', 'folders')} selected
                            </p>
                            <div className="flex items-center gap-2">
                                <ButtonV2
                                    size="xs"
                                    type="hollow"
                                    label="Cancel"
                                    onClick={onClose}
                                    isDisabled={createFileReferencesQuery.isLoading}
                                />
                                <ButtonV2
                                    size="xs"
                                    type="secondary"
                                    label="Create references"
                                    onClick={createReferences}
                                    isDisabled={selectedFolders.length === 0 || createFileReferencesQuery.isLoading}
                                    isLoading={createFileReferencesQuery.isLoading}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </Modal>
    );
}

type FolderRowProps = {
    showPath?: boolean;
    record: FileRecordNew;
    onFolderClick: (record: FileRecordNew) => void;
    onBreadCrumbsClick: (level: string) => void;
};
function FolderRow({ record, onFolderClick, onBreadCrumbsClick, showPath = false }: FolderRowProps): JSX.Element {
    const disabled = record.provider === 'FYI' || record.locked;

    return (
        <div key={record.id} className={`flex items-center gap-4 pl-2`}>
            <div className={disabled ? 'opacity-[0.65]' : ''}>
                <RecordTypeIcon recordType={record.recordType} system={record.systemRecord} />
            </div>

            <div>
                {showPath && (
                    <div>
                        <FilesBreadcrumbs
                            size="small"
                            onLevelSelect={onBreadCrumbsClick}
                            path={record.path.slice(0, record.path.length - 1)}
                        />
                    </div>
                )}
                <TooltipV2 disabled={!disabled} message={'Cannot create reference files in locked or integration folders.'}>
                    <div className="flex gap-2">
                        <p
                            className={`text-left text-sm font-medium cursor-pointer ${
                                disabled ? 'opacity-[0.65] pointer-events-none text-gray-600' : 'text-indigo-600'
                            }`}
                            onClick={disabled ? undefined : (): void => onFolderClick(record)}
                        >
                            {record.name}
                        </p>
                        {record.provider === 'FYI' && <img src={FyiLogo} className="w-6 h-6 shrink-0" />}
                    </div>
                </TooltipV2>
            </div>
        </div>
    );
}

type OtherFileRowProps = {
    record: FileRecordNew;
    showPath?: boolean;
    onBreadCrumbsClick: (level: string) => void;
};

function OtherFileRow({ record, showPath, onBreadCrumbsClick }: OtherFileRowProps): JSX.Element {
    return (
        <div key={record.id} className="flex items-center gap-4 h-8 pl-2 opacity-[0.65] text-gray-600">
            <RecordTypeIcon recordType={record.recordType} system={record.systemRecord} />
            <div>
                {showPath && (
                    <div>
                        <FilesBreadcrumbs
                            size="small"
                            onLevelSelect={onBreadCrumbsClick}
                            path={record.path.slice(0, record.path.length - 1)}
                        />
                    </div>
                )}
                <p className="text-sm text-left font-medium">{record.name}</p>
            </div>
        </div>
    );
}

export default NewReferencesWindow;
